import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

import type { MyChronMapPoint } from '@kangaroo/shared';
import { trackContributionHasGeometry } from '@kangaroo/shared';

import {
  buildCircuitMapPreviewPoints,
  buildContributionCircuitItem,
  type CircuitListItem,
} from '../lib/circuits';
import { readCircuitOutlineCache, writeCircuitOutlineCache } from '../lib/circuitOutlineCache';
import { fetchTrackContributionById, trackContributionDetailKey } from '../lib/trackContributions';

export function useCircuitMapPreviewPoints(circuit: CircuitListItem, maxPoints = 120) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const contribution = circuit.contribution;
  const contributionId = contribution?.id ?? null;
  const contributionUpdatedAt = contribution?.updatedAt ?? null;

  const inlinePoints = useMemo(
    () => buildCircuitMapPreviewPoints(circuit, maxPoints),
    [circuit, maxPoints],
  );

  const cachedPoints = useMemo(() => {
    if (!contributionId || inlinePoints.length >= 2) {
      return null;
    }

    return readCircuitOutlineCache(contributionId, contributionUpdatedAt);
  }, [contributionId, contributionUpdatedAt, inlinePoints.length]);

  const shouldHydrate =
    inlinePoints.length < 2
    && (cachedPoints?.length ?? 0) < 2
    && circuit.source === 'contribution'
    && contributionId
    && contribution
    && (
      trackContributionHasGeometry(contribution.payload)
      || (contribution.payload.polylinePointCount ?? 0) >= 2
      || Boolean(
        contribution.payload.detailedGeometryVersion
        || contribution.payload.detailedGeometryChunkCount,
      )
    );

  useEffect(() => {
    if (!shouldHydrate) {
      return;
    }

    const element = containerRef.current;
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '160px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldHydrate, contributionId]);

  const { data: fetchedContribution, isLoading } = useSWR(
    shouldHydrate && inView ? trackContributionDetailKey(contributionId) : null,
    () => fetchTrackContributionById(contributionId!),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60_000,
    },
  );

  const points = useMemo(() => {
    if (inlinePoints.length >= 2) {
      return inlinePoints;
    }

    if (cachedPoints && cachedPoints.length >= 2) {
      return cachedPoints;
    }

    if (!fetchedContribution) {
      return [];
    }

    const enrichedCircuit = buildContributionCircuitItem(fetchedContribution, circuit.trackName);
    const computed = buildCircuitMapPreviewPoints(enrichedCircuit, maxPoints);
    if (computed.length >= 2 && contributionId) {
      writeCircuitOutlineCache(contributionId, contributionUpdatedAt, computed);
    }

    return computed;
  }, [
    cachedPoints,
    circuit.trackName,
    contributionId,
    contributionUpdatedAt,
    fetchedContribution,
    inlinePoints,
    maxPoints,
  ]);

  return {
    containerRef,
    points,
    loading: shouldHydrate && points.length < 2 && (!inView || isLoading),
  };
}

export type UseCircuitMapPreviewPointsResult = ReturnType<typeof useCircuitMapPreviewPoints>;
